import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { BadgeCheck, ShieldCheck, Star, Truck } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { logClientError } from '@/utils/clientError';
import { usePublicSeo } from '@/utils/seo';

const REVIEW_TAG_LABELS = {
  rapide: 'Rapide',
  ponctuel: 'Ponctuel',
  professionnel: 'Professionnel',
  bon_service: 'Bon service',
  communication: 'Bonne communication',
  retard: 'Retard',
  service_client: 'Service client',
  emballage: 'Emballage soigné',
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const DeliveryReviewPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviewContext, setReviewContext] = useState(null);
  const [pageError, setPageError] = useState('');
  const [successState, setSuccessState] = useState(null);
  const [form, setForm] = useState({
    rating: 0,
    reviewerName: '',
    comment: '',
    tags: [],
  });

  const sellerName = reviewContext?.seller?.name || 'Boutique Coli Noki';
  const courierName = reviewContext?.courier?.name || null;

  usePublicSeo({
    title: sellerName ? `Votre avis pour ${sellerName} | Coli Noki` : 'Laisser un avis | Coli Noki',
    description: 'Partagez rapidement votre expérience de livraison sur Coli Noki avec un lien sécurisé.',
    path: `/review/${token || ''}`,
  });

  const availableTags = useMemo(() => reviewContext?.tag_options || [], [reviewContext]);

  const loadReviewContext = async () => {
    setLoading(true);
    setPageError('');
    try {
      const response = await axios.get(`${API}/review/${token}`);
      setReviewContext(response.data || null);
    } catch (error) {
      logClientError('delivery_review.load_context', error);
      const detail = error.response?.data?.detail || 'Impossible de charger ce lien d’avis.';
      setPageError(detail);
      setReviewContext(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviewContext();
  }, [token]);

  const toggleTag = (tag) => {
    setForm((current) => ({
      ...current,
      tags: current.tags.includes(tag)
        ? current.tags.filter((value) => value !== tag)
        : [...current.tags, tag].slice(0, 5),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }
    if (!form.rating || form.rating < 1 || form.rating > 5) {
      toast.error('Choisissez une note entre 1 et 5 étoiles.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/review/${token}`, {
        rating: form.rating,
        reviewer_name: form.reviewerName || undefined,
        comment: form.comment || undefined,
        tags: form.tags,
      });
      setSuccessState(response.data?.data || null);
      setReviewContext((current) => ({
        ...(current || {}),
        status: 'submitted',
        can_submit: false,
      }));
      toast.success('Merci, votre avis a bien été enregistré.');
    } catch (error) {
      logClientError('delivery_review.submit', error);
      toast.error(error.response?.data?.detail || "Impossible d'envoyer votre avis");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Header />
      <main className="flex-1">
        <div className="container mx-auto space-y-6 px-4 py-8">
          {loading ? (
            <div className="py-16 text-center text-slate-500">Chargement du formulaire d’avis...</div>
          ) : pageError ? (
            <Card>
              <CardHeader>
                <CardTitle>Lien d’avis indisponible</CardTitle>
                <CardDescription>{pageError}</CardDescription>
              </CardHeader>
            </Card>
          ) : !reviewContext ? (
            <Card>
              <CardHeader>
                <CardTitle>Lien d’avis introuvable</CardTitle>
              </CardHeader>
            </Card>
          ) : (
            <>
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="bg-red-100 text-red-700">Avis sécurisé</Badge>
                        {reviewContext.status === 'submitted' && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <BadgeCheck className="mr-1 h-3 w-3" />
                            Avis déjà envoyé
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-2xl">Votre retour sur cette livraison</CardTitle>
                      <CardDescription>
                        Quelques secondes suffisent pour aider la plateforme à valoriser les vendeurs sérieux et les livreurs fiables.
                      </CardDescription>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                      <div><strong>Référence :</strong> {reviewContext.tracking_code || '-'}</div>
                      <div className="mt-1"><strong>Livrée le :</strong> {formatDate(reviewContext.delivered_at)}</div>
                      <div className="mt-1"><strong>Expiration :</strong> {formatDate(reviewContext.expires_at)}</div>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              <div className="grid gap-6 lg:grid-cols-[1fr,0.9fr]">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-cyan-600" />
                      {reviewContext.can_submit ? 'Formulaire d’avis' : 'Avis déjà traité'}
                    </CardTitle>
                    <CardDescription>
                      {reviewContext.can_submit
                        ? 'Votre lien est réservé à cette livraison. Un seul avis est accepté.'
                        : 'Merci, ce lien a déjà été utilisé ou n’est plus disponible.'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {reviewContext.can_submit ? (
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                          <Label>Votre note</Label>
                          <div className="flex items-center gap-2">
                            {Array.from({ length: 5 }).map((_, index) => {
                              const starValue = index + 1;
                              const isActive = starValue <= form.rating;
                              return (
                                <button
                                  key={`review-star-${starValue}`}
                                  type="button"
                                  className={`rounded-full p-1 transition ${isActive ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
                                  onClick={() => setForm((current) => ({ ...current, rating: starValue }))}
                                  aria-label={`Donner ${starValue} étoile${starValue > 1 ? 's' : ''}`}
                                >
                                  <Star className={`h-7 w-7 ${isActive ? 'fill-current' : ''}`} />
                                </button>
                              );
                            })}
                            <span className="text-sm text-slate-500">{form.rating > 0 ? `${form.rating}/5` : 'Choisissez une note'}</span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="reviewerName">Prénom ou nom (optionnel)</Label>
                          <Input
                            id="reviewerName"
                            value={form.reviewerName}
                            onChange={(event) => setForm((current) => ({ ...current, reviewerName: event.target.value }))}
                            placeholder="Ex. Aline"
                          />
                        </div>

                        <div className="space-y-3">
                          <Label>Ce qui vous a marqué</Label>
                          <div className="flex flex-wrap gap-2">
                            {availableTags.map((tag) => {
                              const active = form.tags.includes(tag);
                              return (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => toggleTag(tag)}
                                  className={`rounded-full border px-3 py-1.5 text-sm transition ${
                                    active
                                      ? 'border-red-200 bg-red-50 text-red-700'
                                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                                  }`}
                                >
                                  {REVIEW_TAG_LABELS[tag] || tag}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="reviewComment">Commentaire (optionnel)</Label>
                          <Textarea
                            id="reviewComment"
                            rows={5}
                            value={form.comment}
                            onChange={(event) => setForm((current) => ({ ...current, comment: event.target.value }))}
                            placeholder="Décrivez votre expérience en quelques mots."
                          />
                        </div>

                        <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={submitting}>
                          {submitting ? 'Envoi en cours...' : 'Envoyer mon avis'}
                        </Button>
                      </form>
                    ) : (
                      <div className="space-y-4 rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-900">
                        <p className="font-semibold">Merci, votre avis a déjà été pris en compte.</p>
                        <p>Votre retour contribue à renforcer la confiance autour des livraisons Coli Noki.</p>
                        {successState?.seller_reputation && (
                          <div className="rounded-lg border border-emerald-100 bg-white p-3 text-slate-700">
                            <p className="font-medium text-slate-900">Réputation vendeur mise à jour</p>
                            <p className="mt-1 text-sm">
                              {Number(successState.seller_reputation.average_rating || 0).toFixed(2)} / 5
                              <span className="ml-2 text-slate-500">({successState.seller_reputation.total_reviews || 0} avis)</span>
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Contexte de la livraison</CardTitle>
                    <CardDescription>Les informations utiles pour vous repérer rapidement avant de noter l’expérience.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-sm font-medium text-slate-500">Boutique</p>
                      <p className="mt-1 text-lg font-semibold text-slate-900">{sellerName}</p>
                    </div>
                    {courierName && (
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
                          <Truck className="h-4 w-4" />
                          Livreur
                        </div>
                        <p className="mt-1 text-lg font-semibold text-slate-900">{courierName}</p>
                      </div>
                    )}
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                      <p>Votre commentaire reste optionnel. Une note claire et quelques tags suffisent déjà à être utile.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
      <FooterNew />
    </div>
  );
};

export default DeliveryReviewPage;
